import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import {
  ADMIN_JIT_ACTIVATIONS_QUERY_KEY,
  ADMIN_JIT_MY_ACCESS_QUERY_KEY,
  adminJitApi,
  type AdminJitActivation,
} from '../api/adminConsole';
import { ADMIN_CONSOLE_ME_QUERY_KEY } from '../lib/adminSession';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { formatIamTimestamp } from '../lib/iamAssignmentStatus';
import {
  activationStatusChip,
  approvalRows,
  activationPasskeyNotice,
  canActivate,
  defaultActivationDuration,
  durationOptions,
  formatDuration,
  jitModeNotice,
  myAccessRows,
  validateActivationDraft,
  type MyAccessRow,
} from '../lib/jitAccess';

type TabValue = 'mine' | 'approvals';

/** Re-render every 30s so the time left stays current without refetching. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/**
 * Just-in-time access (RBAC Phase 4 workstream A). "My roles" lists the roles the viewer is
 * eligible for — activate one with a reason, cancel a pending request, end an active one early.
 * "Approvals" lists other people's pending requests the viewer may decide. admin-api makes every
 * decision (eligibility, approver, no self-approval, MFA); this page only shows and asks.
 */
export function MyAccessPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const tab: TabValue = location.pathname.endsWith('/approvals') ? 'approvals' : 'mine';
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const now = useNow();

  const accessQ = useQuery({
    queryKey: ADMIN_JIT_MY_ACCESS_QUERY_KEY,
    queryFn: () => adminJitApi.myAccess(),
    enabled: hasToken,
    refetchInterval: 60_000,
  });
  const historyQ = useQuery({
    queryKey: [...ADMIN_JIT_ACTIVATIONS_QUERY_KEY, 'mine'],
    queryFn: () => adminJitApi.activations('mine'),
    enabled: hasToken && tab === 'mine',
  });
  const approvableQ = useQuery({
    queryKey: [...ADMIN_JIT_ACTIVATIONS_QUERY_KEY, 'approvable'],
    queryFn: () => adminJitApi.activations('approvable'),
    enabled: hasToken,
    refetchInterval: 60_000,
  });

  const mode = accessQ.data?.mode;
  const rows = useMemo(() => myAccessRows(accessQ.data, now), [accessQ.data, now]);
  const approvals = useMemo(() => approvalRows(approvableQ.data?.items ?? [], now), [approvableQ.data, now]);
  const notice = accessQ.data ? jitModeNotice(mode) : null;

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ADMIN_JIT_MY_ACCESS_QUERY_KEY }),
      qc.invalidateQueries({ queryKey: ADMIN_JIT_ACTIVATIONS_QUERY_KEY }),
      // An activation changes the viewer's permissions (nav, page gates).
      qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY }),
    ]);
  };

  const [activating, setActivating] = useState<MyAccessRow | null>(null);
  const [draft, setDraft] = useState({ reason: '', ticket: '', durationMinutes: 240 });
  const [draftErrors, setDraftErrors] = useState<{ reason?: string; durationMinutes?: string }>({});
  const request = useMutation({
    mutationFn: (body: Parameters<typeof adminJitApi.request>[0]) => adminJitApi.request(body),
    onSuccess: async () => {
      setActivating(null);
      await refresh();
    },
  });
  const rowAction = useMutation({
    mutationFn: (input: { id: string; action: 'cancel' | 'end' }) =>
      input.action === 'cancel' ? adminJitApi.cancel(input.id) : adminJitApi.end(input.id),
    onSuccess: refresh,
  });

  const [deciding, setDeciding] = useState<{ item: AdminJitActivation; decision: 'approve' | 'reject' } | null>(null);
  const [comment, setComment] = useState('');
  const decide = useMutation({
    mutationFn: (input: { id: string; decision: 'approve' | 'reject'; comment: string }) =>
      adminJitApi.decide(input.id, input.decision, input.comment.trim() || undefined),
    onSuccess: async () => {
      setDeciding(null);
      await refresh();
    },
  });

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to view your access.</Alert>
      </Box>
    );
  }

  const openActivate = (row: MyAccessRow) => {
    request.reset();
    setDraftErrors({});
    setDraft({ reason: '', ticket: '', durationMinutes: defaultActivationDuration(row.maxDurationMinutes) });
    setActivating(row);
  };

  const submitActivate = () => {
    if (!activating) return;
    const v = validateActivationDraft(draft, activating);
    if (!v.ok) return void setDraftErrors(v.errors);
    setDraftErrors({});
    request.mutate(v.body);
  };

  return (
    <Box>
      <AdminListPageHeader
        title="My access"
        subtitle="Critical roles are activated when you need them, for a few hours, with a reason — and a second person's approval where required."
      />
      <Tabs
        value={tab}
        onChange={(_, v: TabValue) => navigate(v === 'approvals' ? '/my-access/approvals' : '/my-access')}
        sx={{ mb: 2 }}
      >
        <Tab value="mine" label="My roles" />
        <Tab
          value="approvals"
          label={approvals.length > 0 ? `Approvals (${approvals.length})` : 'Approvals'}
        />
      </Tabs>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }}>
          {notice.text}
        </Alert>
      )}
      <IamErrorAlert error={accessQ.error ?? rowAction.error} sx={{ mb: 2 }} />

      {tab === 'mine' ? (
        <>
          <Card variant="outlined" sx={dataTableCardSx(theme)}>
            {accessQ.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small" sx={adminDataTableSx(theme)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Policy</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const gate = canActivate(mode, r);
                    const passkeyNotice = activationPasskeyNotice(r);
                    return (
                      <TableRow key={r.eligibilityId}>
                        <TableCell>{r.roleName}</TableCell>
                        <TableCell>
                          {r.state === 'active' ? (
                            <Stack direction="row" gap={1} alignItems="center">
                              <Chip size="small" color="success" variant="outlined" label={`Active · ${r.remaining} left`} />
                              {r.reportOnly && (
                                <Tooltip title="You also hold this role standing, so the activation was recorded but changed nothing.">
                                  <Chip size="small" variant="outlined" label="Report-only" />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : r.state === 'pending' ? (
                            <Chip size="small" color="warning" variant="outlined" label="Waiting for approval" />
                          ) : (
                            <Chip size="small" variant="outlined" label="Eligible" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            Up to {formatDuration(r.maxDurationMinutes)}
                            {r.requiresApproval ? ' · needs approval' : ''}
                          </Typography>
                          {passkeyNotice && (
                            <Tooltip title={passkeyNotice}>
                              <Chip size="small" color="info" variant="outlined" label="Needs passkey sign-in" sx={{ mt: 0.5 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {r.state === 'eligible' && (
                            <Tooltip title={gate.allowed ? '' : gate.reason}>
                              <span>
                                <Button size="small" variant="contained" disabled={!gate.allowed} onClick={() => openActivate(r)}>
                                  Activate
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {r.state === 'pending' && r.activationId && (
                            <Button
                              size="small"
                              color="inherit"
                              disabled={rowAction.isPending}
                              onClick={() => rowAction.mutate({ id: r.activationId!, action: 'cancel' })}
                            >
                              Cancel request
                            </Button>
                          )}
                          {r.state === 'active' && r.activationId && (
                            <Button
                              size="small"
                              color="warning"
                              disabled={rowAction.isPending}
                              onClick={() => rowAction.mutate({ id: r.activationId!, action: 'end' })}
                            >
                              End now
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          You are not eligible for any role that is activated just in time.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Card>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            My recent requests
          </Typography>
          <Card variant="outlined" sx={dataTableCardSx(theme)}>
            <Table size="small" sx={adminDataTableSx(theme)}>
              <TableHead>
                <TableRow>
                  <TableCell>Requested</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Decided by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(historyQ.data?.items ?? []).map((a) => {
                  const chip = activationStatusChip(a.displayStatus);
                  return (
                    <TableRow key={a.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIamTimestamp(a.requestedAt)}</TableCell>
                      <TableCell>{a.roleName ?? a.roleKey}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" noWrap title={a.reason ?? ''}>
                          {a.reason || '—'}
                          {a.ticket ? ` (${a.ticket})` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={a.decisionComment ?? ''}>
                          {a.decidedByEmail ?? (a.requiresApproval ? '—' : 'No approval needed')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(historyQ.data?.items ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No requests yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      ) : (
        <>
          <IamErrorAlert error={approvableQ.error} sx={{ mb: 2 }} />
          <Card variant="outlined" sx={dataTableCardSx(theme)}>
            {approvableQ.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small" sx={adminDataTableSx(theme)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Requested</TableCell>
                    <TableCell>Who</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Expires undecided in</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvals.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={formatIamTimestamp(a.requestedAt)}>
                          <span>{a.requestedAgo}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {a.userDisplayName || a.userEmail || a.userId}
                        {a.userEmail && a.userDisplayName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {a.userEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{a.roleName ?? a.roleKey}</TableCell>
                      <TableCell>{formatDuration(a.durationMinutes)}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" title={a.reason ?? ''}>
                          {a.reason || '—'}
                        </Typography>
                        {a.ticket && (
                          <Typography variant="caption" color="text.secondary">
                            Ticket {a.ticket}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{a.expiresIn}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            decide.reset();
                            setComment('');
                            setDeciding({ item: a, decision: 'approve' });
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            decide.reset();
                            setComment('');
                            setDeciding({ item: a, decision: 'reject' });
                          }}
                        >
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {approvals.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">
                          Nothing waiting for your approval.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      )}

      <Dialog open={Boolean(activating)} onClose={() => setActivating(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Activate {activating?.roleName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={request.error} />
            {activating?.requiresApproval && (
              <Alert severity="info">This role needs approval. It becomes active once someone else approves your request.</Alert>
            )}
            <TextField
              select
              size="small"
              label="For"
              value={draft.durationMinutes}
              onChange={(e) => setDraft((d) => ({ ...d, durationMinutes: Number(e.target.value) }))}
              error={Boolean(draftErrors.durationMinutes)}
              helperText={draftErrors.durationMinutes}
            >
              {durationOptions(activating?.maxDurationMinutes ?? 240).map((m) => (
                <MenuItem key={m} value={m}>
                  {formatDuration(m)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={activating?.reasonRequired ? 'Reason (required)' : 'Reason'}
              multiline
              minRows={2}
              value={draft.reason}
              onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
              error={Boolean(draftErrors.reason)}
              helperText={draftErrors.reason ?? 'Recorded in the audit log and shown to the approver.'}
            />
            <TextField
              size="small"
              label="Ticket (optional)"
              value={draft.ticket}
              onChange={(e) => setDraft((d) => ({ ...d, ticket: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivating(null)}>Cancel</Button>
          <Button variant="contained" disabled={request.isPending} onClick={submitActivate}>
            {activating?.requiresApproval ? 'Request' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deciding)} onClose={() => setDeciding(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{deciding?.decision === 'approve' ? 'Approve request' : 'Reject request'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={decide.error} />
            <Typography variant="body2">
              <strong>{deciding?.item.roleName ?? deciding?.item.roleKey}</strong> for {deciding?.item.userEmail ?? 'this user'}, {deciding ? formatDuration(deciding.item.durationMinutes) : ''} —{' '}
              {deciding?.item.reason || 'no reason given'}
            </Typography>
            <TextField
              size="small"
              label="Comment (optional)"
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeciding(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={deciding?.decision === 'approve' ? 'primary' : 'error'}
            disabled={decide.isPending}
            onClick={() => deciding && decide.mutate({ id: deciding.item.id, decision: deciding.decision, comment })}
          >
            {deciding?.decision === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
